import React, { useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Fade,
  Button
} from '@mui/material';
import {
  CheckCircle,
  Visibility,
  TrendingUp,
  Favorite,
  Build,
  Settings,
} from '@mui/icons-material';
import LoadingImage from '../components/LoadingImage';

const About: React.FC = () => {
  // SEO Meta Tags
  useEffect(() => {
    document.title = "Hakkımızda - Kale Treyler Frigo | Mersin'in Güvenilir Frigo Dorse Tamir Servisi";
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', "Kale Treyler Frigo hakkında bilgi edinin. Mersin'de frigo dorse tamiri konusunda uzman ekibimiz ve kaliteli hizmet anlayışımızla müşterilerimize hizmet veriyoruz.");
    }
    
    // Update meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', "Kale Treyler hakkında, frigo dorse tamiri Mersin, güvenilir servis, uzman ekip, kaliteli hizmet");
    }
    
    // Update Open Graph tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', "Hakkımızda - Kale Treyler Frigo | Mersin'in Güvenilir Frigo Dorse Tamir Servisi");
    }
    
    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', "Kale Treyler Frigo hakkında bilgi edinin. Mersin'de frigo dorse tamiri konusunda uzman ekibimiz ve kaliteli hizmet anlayışımızla müşterilerimize hizmet veriyoruz.");
    }

    // Update Canonical URL
    const canonicalLink = (document.querySelector('link[rel="canonical"]') || document.createElement('link')) as HTMLLinkElement;
    canonicalLink.setAttribute('rel', 'canonical');
    canonicalLink.setAttribute('href', 'https://kaletreyler.com/hakkimizda');
    if (!document.head.contains(canonicalLink)) {
      document.head.appendChild(canonicalLink);
    }
  }, []);

  const values = [
    {
      icon: <CheckCircle sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'Kaliteli Tamir',
      description: 'Frigo dorse kasalarında en yüksek kalite standartlarında tamir hizmeti',
    },
    {
      icon: <Favorite sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'Güvenilirlik',
      description: 'Müşterilerimizle güvene dayalı uzun vadeli servis ilişkileri',
    },
    {
      icon: <TrendingUp sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'Sürekli Gelişim',
      description: 'Teknoloji ve teknik bilgiyi sürekli geliştirerek öne çıkıyoruz',
    },
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          minHeight: '60vh',
          background: 'linear-gradient(135deg, #ffffff 0%, #f1f5f9 50%, #e2e8f0 100%)',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 20% 80%, rgba(30, 58, 138, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(30, 58, 138, 0.1) 0%, transparent 50%)',
            zIndex: 0,
          }
        }}
      >
        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1, px: { xs: 2, md: 4, lg: 6 } }}>
          <Grid container spacing={{ xs: 4, md: 6, lg: 8 }} alignItems="center">
            <Grid item xs={12} lg={6}>
              <Fade in timeout={1000}>
                <Box>
                  <Typography variant="h1" component="h1" gutterBottom sx={{ 
                    fontWeight: 800, 
                    color: 'text.primary',
                    fontSize: { xs: '2.5rem', md: '3.5rem', lg: '4rem' },
                    lineHeight: 1.1,
                    mb: 3
                  }}>
                    Hakkımızda
                  </Typography>
                  <Typography variant="h5" paragraph sx={{ 
                    mb: 4, 
                    color: 'text.secondary',
                    lineHeight: 1.6
                  }}>
                    Kale Treyler olarak, frigo dorse tamirinde uzman ekibimizle güvenilir ve kaliteli servis hizmeti sunuyoruz
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircle sx={{ color: 'primary.main', fontSize: 20 }} />
                      <Typography variant="body1" color="text.secondary">
                       
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircle sx={{ color: 'primary.main', fontSize: 20 }} />
                      <Typography variant="body1" color="text.secondary">
                        
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircle sx={{ color: 'primary.main', fontSize: 20 }} />
                      <Typography variant="body1" color="text.secondary">
                        
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Fade>
            </Grid>
            <Grid item xs={12} lg={6}>
              <Fade in timeout={1500}>
                <Box
                  sx={{
                    position: 'relative',
                    borderRadius: 3,
                    overflow: 'hidden',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                    '&:hover': {
                      transform: 'scale(1.02)',
                      transition: 'transform 0.3s ease',
                    }
                  }}
                >
                  <LoadingImage
                    src="/18.jpeg"
                    alt="Kale Treyler İşletme Görseli"
                    style={{
                      width: '100%',
                      height: '400px',
                      objectFit: 'cover',
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
                      p: 3,
                      color: 'white',
                    }}
                  >
                    <Typography variant="h5" component="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                      Kale Treyler
                    </Typography>
                    <Typography variant="body1" sx={{ opacity: 0.9 }}>
                      Frigo dorse tamirinde uzman servis merkezi
                    </Typography>
                  </Box>
                </Box>
              </Fade>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Company Story */}
      <Container maxWidth="xl" sx={{ py: 12, px: { xs: 2, md: 4, lg: 6 } }}>
        <Fade in timeout={1000}>
          <Box textAlign="center" maxWidth="800px" mx="auto">
            <Typography variant="h3" component="h2" gutterBottom color="primary" sx={{ fontWeight: 'bold', mb: 4 }}>
              Biz Kimiz?
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8, fontSize: '1.1rem', mb: 3 }}>
              Kale Treyler olarak frigo dorse tamirinde uzman ekibimizle müşterilerimize 
              en kaliteli servis hizmetlerini sunmaktadır. Müşteri memnuniyetini her zaman ön planda tutarak, 
              güvenilir ve sürdürülebilir tamir hizmeti anlayışımızla sektörde öncü konumda yer almaktayız.
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8, fontSize: '1.1rem', mb: 4 }}>
              Modern tamir teknolojileri ve uzman teknisyen kadromuzla, her frigo dorse kasasına özel 
              çözümler üretiyor ve müşterilerimizin acil ihtiyaçlarını en hızlı şekilde karşılıyoruz.
            </Typography>
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', justifyContent: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Build sx={{ color: 'primary.main', fontSize: 24 }} />
                <Typography variant="body1" color="text.secondary">
                  Uzman Teknisyenler
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Settings sx={{ color: 'primary.main', fontSize: 24 }} />
                <Typography variant="body1" color="text.secondary">
                  Modern Teknoloji
                </Typography>
              </Box>
            </Box>
          </Box>
        </Fade>
      </Container>

      {/* Mission & Vision */}
      <Box sx={{ bgcolor: 'grey.50', py: 12 }}>
        <Container maxWidth="xl" sx={{ px: { xs: 2, md: 4, lg: 6 } }}>
          <Box textAlign="center" mb={8}>
            <Typography variant="h3" component="h2" gutterBottom color="primary" sx={{ fontWeight: 'bold' }}>
              Misyonumuz & Vizyonumuz
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: '600px', mx: 'auto' }}>
              İşimizi yaparken her zaman bu değerlerimizi ön planda tutuyoruz
            </Typography>
          </Box>
          <Grid container spacing={{ xs: 4, md: 6, lg: 8 }}>
            <Grid item xs={12} md={6}>
              <Card elevation={0} sx={{ 
                height: '100%', 
                p: 4,
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'grey.200',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                  borderColor: 'primary.main',
                }
              }}>
                <CardContent>
                  <Box mb={3} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ 
                      p: 2, 
                      borderRadius: '50%', 
                      bgcolor: 'primary.main',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <TrendingUp sx={{ fontSize: 30 }} />
                    </Box>
                    <Typography variant="h4" component="h3" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                      Misyonumuz
                    </Typography>
                  </Box>
                  <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8, fontSize: '1.1rem' }}>
                    Frigo dorse kasalarında en kaliteli tamir hizmetlerini sunarak, müşterilerimizin 
                    işlerini kesintisiz sürdürmelerine yardımcı olmak ve sektörde güvenilir bir servis partneri olmak.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card elevation={0} sx={{ 
                height: '100%', 
                p: 4,
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'grey.200',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                  borderColor: 'primary.main',
                }
              }}>
                <CardContent>
                  <Box mb={3} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ 
                      p: 2, 
                      borderRadius: '50%', 
                      bgcolor: 'primary.main',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Visibility sx={{ fontSize: 30 }} />
                    </Box>
                    <Typography variant="h4" component="h3" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                      Vizyonumuz
                    </Typography>
                  </Box>
                  <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8, fontSize: '1.1rem' }}>
                    Frigo dorse tamirinde Mersin'in en güvenilir servis merkezi olarak, 
                    teknolojik yeniliklerle müşterilerimizin başarısına katkıda bulunuyoruz.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Values */}
      <Container maxWidth="xl" sx={{ py: 12, px: { xs: 2, md: 4, lg: 6 } }}>
        <Box textAlign="center" mb={8}>
          <Typography variant="h3" component="h2" gutterBottom color="primary" sx={{ fontWeight: 'bold' }}>
            Değerlerimiz
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: '600px', mx: 'auto' }}>
            İşimizi yaparken her zaman bu değerlerimizi ön planda tutuyoruz
          </Typography>
        </Box>

        <Grid container spacing={{ xs: 4, md: 6, lg: 8 }}>
          {values.map((value, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  textAlign: 'center',
                  p: 4,
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'grey.200',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                    borderColor: 'primary.main',
                  },
                }}
              >
                <CardContent>
                  <Box 
                    mb={3}
                    sx={{
                      display: 'inline-flex',
                      p: 3,
                      borderRadius: '50%',
                      bgcolor: 'primary.main',
                      color: 'white',
                      mx: 'auto'
                    }}
                  >
                    {value.icon}
                  </Box>
                  <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
                    {value.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8, fontSize: '1.1rem' }}>
                    {value.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Blog Redirection Section */}
      <Box sx={{ bgcolor: 'primary.main', color: 'white', py: 8, mt: 8, textAlign: 'center' }}>
        <Container maxWidth="md">
          <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
            Dorse Bakımı ve Teknik Detaylar
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, opacity: 0.9, fontSize: '1.1rem' }}>
            Frigo dorse kasaları, yalıtım sistemleri ve tır bakım ipuçları hakkında hazırladığımız sektörel paylaşımlarımıza göz atın.
          </Typography>
          <Button
            component={RouterLink}
            to="/blog"
            variant="contained"
            color="secondary"
            size="large"
            sx={{
              px: 6,
              py: 2,
              fontSize: '1.1rem',
              fontWeight: 'bold',
              borderRadius: 2,
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 20px rgba(220, 38, 38, 0.4)',
              },
              transition: 'all 0.3s ease'
            }}
          >
            Blog Sayfamızı Ziyaret Edin
          </Button>
        </Container>
      </Box>
    </Box>
  );
};

export default About; 